import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Send, Bot, ClipboardPaste, GitCompareArrows, Save, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { VideoResultCard } from "./VideoResultCard";
import { SummaryDiffView } from "./SummaryDiffView";
import { extractVideoId, streamSummary } from "./utils";
import { autoSaveSummary } from "./autoSave";
import type { VideoResult } from "./types";

const MODEL_OPTIONS = [
  { value: "gemini-3.1-pro", label: "🏆 Gemini 3.1 Pro" },
  { value: "gemini-2.5-pro", label: "⚡ Gemini 2.5 Pro" },
  { value: "gemini-3-flash", label: "🚀 Gemini 3 Flash" },
  { value: "gemini-2.5-flash", label: "💡 Gemini 2.5 Flash" },
  { value: "gemini-2.5-flash-lite", label: "🪶 Gemini 2.5 Flash Lite" },
  { value: "gpt-5.2", label: "🧠 GPT-5.2" },
  { value: "gpt-5", label: "🎯 GPT-5" },
  { value: "gpt-5-mini", label: "💨 GPT-5 Mini" },
];

interface Props {
  userId?: string;
}

export function CompareMode({ userId }: Props) {
  const [url, setUrl] = useState("");
  const [modelA, setModelA] = useState("gemini-2.5-pro");
  const [modelB, setModelB] = useState("gpt-5");
  const [resultA, setResultA] = useState<VideoResult | null>(null);
  const [resultB, setResultB] = useState<VideoResult | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [savingAll, setSavingAll] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const { toast } = useToast();

  const bothDone = resultA?.status === "done" && resultB?.status === "done";
  const bothSaved = resultA?.saved && resultB?.saved;

  const handleSaveAll = async () => {
    if (!userId) {
      toast({ title: "Sign in required", description: "Please sign in to save summaries.", variant: "destructive" });
      return;
    }
    if (!resultA || !resultB) return;
    setSavingAll(true);
    const rows = [resultA, resultB]
      .filter(r => !r.saved)
      .map(r => ({
        user_id: userId,
        video_url: r.url,
        video_id: r.videoId,
        summary_content: r.summary,
        author: r.author || null,
      }));
    if (rows.length === 0) {
      setSavingAll(false);
      return;
    }
    const { error } = await supabase.from("content_summaries").insert(rows);
    setSavingAll(false);
    if (error) {
      toast({ title: "Save failed", description: error.message, variant: "destructive" });
    } else {
      setResultA(prev => prev ? { ...prev, saved: true } : null);
      setResultB(prev => prev ? { ...prev, saved: true } : null);
      toast({ title: "Both saved", description: "Both summaries saved to Collected Data." });
    }
  };

  const isValidYoutubeUrl = (u: string) =>
    /(?:youtube\.com\/(?:watch|embed|shorts)|youtu\.be\/)/.test(u);

  const handleCompare = async () => {
    const trimmed = url.trim();
    if (!trimmed || !isValidYoutubeUrl(trimmed)) {
      toast({ title: "Invalid URL", description: "Please enter a valid YouTube video URL.", variant: "destructive" });
      return;
    }
    if (modelA === modelB) {
      toast({ title: "Same models", description: "Please select two different models to compare.", variant: "destructive" });
      return;
    }

    const videoId = extractVideoId(trimmed);
    const baseA: VideoResult = { id: `cmp-a-${Date.now()}`, url: trimmed, videoId, summary: "", status: "loading" };
    const baseB: VideoResult = { id: `cmp-b-${Date.now()}`, url: trimmed, videoId, summary: "", status: "loading" };
    setResultA(baseA);
    setResultB(baseB);
    setIsProcessing(true);
    abortRef.current = new AbortController();

    const runStream = async (
      model: string,
      setResult: React.Dispatch<React.SetStateAction<VideoResult | null>>,
      base: VideoResult
    ) => {
      try {
        const result = await streamSummary(
          trimmed,
          (text) => setResult(prev => prev ? { ...prev, summary: text } : null),
          abortRef.current!.signal,
          (meta) => setResult(prev => prev ? { ...prev, author: meta.author, model: meta.model, language: meta.language } : null),
          model
        );
        const limitedInfo = /captions were unavailable|no captions available|no captions or description|based on.*title alone/i.test(result.summary);
        setResult(prev => prev ? { ...prev, summary: result.summary, status: "done", limitedInfo, author: result.author, model: result.model, language: result.language } : null);
        // Auto-save
        const didSave = await autoSaveSummary({
          userId,
          videoUrl: trimmed,
          videoId: extractVideoId(trimmed),
          summaryContent: result.summary,
          author: result.author,
        });
        if (didSave) {
          setResult(prev => prev ? { ...prev, saved: true } : null);
        }
      } catch (error: any) {
        if (error.name !== "AbortError") {
          setResult(prev => prev ? { ...prev, status: "error", error: error.message } : null);
        }
      }
    };

    await Promise.all([
      runStream(modelA, setResultA, baseA),
      runStream(modelB, setResultB, baseB),
    ]);
    setIsProcessing(false);
  };

  const handleCancel = () => {
    abortRef.current?.abort();
    setIsProcessing(false);
  };

  return (
    <div className="space-y-6">
      <Card className="border-primary/10">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <GitCompareArrows className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">Compare Models Side by Side</CardTitle>
          </div>
          <p className="text-sm text-muted-foreground">
            Analyze the same video with two different AI models and compare the results.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* URL Input */}
          <div className="flex gap-2">
            <Input
              placeholder="https://www.youtube.com/watch?v=..."
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              disabled={isProcessing}
              className="flex-1"
            />
            <Button
              variant="outline"
              size="icon"
              disabled={isProcessing}
              title="Paste from clipboard"
              onClick={async () => {
                try {
                  const text = await navigator.clipboard.readText();
                  if (text.trim()) { setUrl(text.trim()); toast({ title: "Pasted" }); }
                } catch { toast({ title: "Clipboard access denied", variant: "destructive" }); }
              }}
            >
              <ClipboardPaste className="h-4 w-4" />
            </Button>
          </div>

          {/* Model Selectors */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                <Bot className="h-3.5 w-3.5" /> Model A
              </label>
              <Select value={modelA} onValueChange={setModelA} disabled={isProcessing}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MODEL_OPTIONS.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                <Bot className="h-3.5 w-3.5" /> Model B
              </label>
              <Select value={modelB} onValueChange={setModelB} disabled={isProcessing}>
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MODEL_OPTIONS.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 justify-end">
            {isProcessing && (
              <Button variant="outline" size="sm" onClick={handleCancel}>Cancel</Button>
            )}
            <Button
              onClick={handleCompare}
              disabled={isProcessing || !url.trim()}
              className="bg-primary hover:bg-primary/90"
            >
              {isProcessing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <GitCompareArrows className="h-4 w-4 mr-2" />}
              {isProcessing ? "Comparing…" : "Compare"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Save All Button */}
      {bothDone && (
        <div className="flex items-center justify-end gap-3">
          {bothSaved && (
            <Badge variant="default" className="gap-1 text-xs bg-emerald-600">
              <Check className="h-3 w-3" /> Both Saved
            </Badge>
          )}
          {!bothSaved && (
            <>
              {resultA?.saved && (
                <Badge variant="outline" className="gap-1 text-xs border-emerald-500/30 text-emerald-600 dark:text-emerald-400">
                  <Check className="h-3 w-3" /> Model A Saved
                </Badge>
              )}
              {resultB?.saved && (
                <Badge variant="outline" className="gap-1 text-xs border-emerald-500/30 text-emerald-600 dark:text-emerald-400">
                  <Check className="h-3 w-3" /> Model B Saved
                </Badge>
              )}
              <Button onClick={handleSaveAll} disabled={savingAll} className="gap-2">
                {savingAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {savingAll ? "Saving…" : resultA?.saved || resultB?.saved ? "Save Remaining" : "Save Both Summaries"}
              </Button>
            </>
          )}
        </div>
      )}

      {/* Side by Side Results */}
      {(resultA || resultB) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Badge variant={resultA?.saved ? "default" : "outline"} className={`text-xs gap-1 ${resultA?.saved ? "bg-emerald-600" : ""}`}>
              {resultA?.saved ? <Check className="h-3 w-3" /> : <Bot className="h-3 w-3" />}
              Model A {resultA?.saved ? "✓" : ""}
            </Badge>
            {resultA && (
              <VideoResultCard
                result={resultA}
                userId={userId}
                onSaved={() => setResultA(prev => prev ? { ...prev, saved: true } : null)}
              />
            )}
          </div>
          <div className="space-y-2">
            <Badge variant={resultB?.saved ? "default" : "outline"} className={`text-xs gap-1 ${resultB?.saved ? "bg-emerald-600" : ""}`}>
              {resultB?.saved ? <Check className="h-3 w-3" /> : <Bot className="h-3 w-3" />}
              Model B {resultB?.saved ? "✓" : ""}
            </Badge>
            {resultB && (
              <VideoResultCard
                result={resultB}
                userId={userId}
                onSaved={() => setResultB(prev => prev ? { ...prev, saved: true } : null)}
              />
            )}
          </div>
        </div>
      )}

      {/* Diff View */}
      {resultA?.status === "done" && resultB?.status === "done" && (
        <SummaryDiffView
          summaryA={resultA.summary}
          summaryB={resultB.summary}
          modelA={resultA.model || MODEL_OPTIONS.find(m => m.value === modelA)?.label}
          modelB={resultB.model || MODEL_OPTIONS.find(m => m.value === modelB)?.label}
        />
      )}
    </div>
  );
}
